// Iter UAT-2 (2026-06-29) — Attendance Diagnostic (PSA-only, read-only).
//
// Route: /admin/attendance-diagnostic
//
// Mirrors the on-container CLI script `econ2_diagnostic.py` so the
// operator can run the diagnostic from the browser without SSH access.
// The page renders the same 9-block text exactly as the script would
// print it (`<pre>` block) so the output can be pasted back unchanged.
//
// READ-ONLY: every endpoint hit is GET. No writes, no policy / employee
// / scheduler change. PSA-gated on the backend.
import { useEffect, useState } from "react";
import { MagnifyingGlass, ShieldWarning, Info, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useAuth } from "../../contexts/AuthContext";

export default function AttendanceDiagnostic() {
    const { user } = useAuth();
    const isPsa = user?.is_primary_admin === true;
    const [email, setEmail] = useState("");
    const [empId, setEmpId] = useState("");
    const [running, setRunning] = useState(false);
    const [report, setReport] = useState(null);
    const [dateInput, setDateInput] = useState(
        new Date().toISOString().substring(0, 10),
    );
    const [snapshotsSummary, setSnapshotsSummary] = useState(null);
    const [missingSnapshots, setMissingSnapshots] = useState(null);
    const [schedulerState, setSchedulerState] = useState(null);
    const [overrideInventory, setOverrideInventory] = useState(null);
    const [overrideLoading, setOverrideLoading] = useState(false);
    // Iter UAT-3 / Phase 2 verification — "Rebuild this snapshot" affordance
    // over the existing POST /api/attendance/snapshot/rebuild endpoint. PSA
    // (via employee:admin on the backend) only. No new business logic.
    const [rebuildDate, setRebuildDate] = useState(
        new Date().toISOString().substring(0, 10),
    );
    const [rebuildConfirmOpen, setRebuildConfirmOpen] = useState(false);
    const [rebuilding, setRebuilding] = useState(false);
    const [rebuildResult, setRebuildResult] = useState(null);

    // Initial scheduler/health check — runs once on page load (PSA only).
    useEffect(() => {
        if (!isPsa) return;
        (async () => {
            try {
                const res = await api.get(
                    "/admin/attendance-diagnostic/scheduler-state",
                );
                setSchedulerState(res.data);
            } catch (err) {
                // Silent — not critical for the page to function.
                console.warn("scheduler-state lookup failed:", err);
            }
        })();
    }, [isPsa]);

    const runOverrideInventory = async () => {
        setOverrideLoading(true);
        setOverrideInventory(null);
        try {
            const res = await api.get(
                "/admin/attendance-diagnostic/override-inventory",
            );
            setOverrideInventory(res.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setOverrideLoading(false);
        }
    };

    if (!isPsa) {
        return (
            <div className="p-8">
                <div className="max-w-xl mx-auto rounded-lg border border-amber-200 bg-amber-50 p-6">
                    <div className="flex items-center gap-3 mb-2">
                        <ShieldWarning size={20} className="text-amber-700" />
                        <h2 className="text-lg font-semibold text-amber-900">
                            Primary super-admin only
                        </h2>
                    </div>
                    <p className="text-sm text-amber-800">
                        This diagnostic page is restricted to the Primary
                        Super-Admin (PSA) account. All endpoints are read-only,
                        but visibility is intentionally restricted.
                    </p>
                </div>
            </div>
        );
    }

    const runEmployee = async () => {
        if (!email && !empId) {
            toast.error("Provide either an email or an employee id");
            return;
        }
        setRunning(true);
        setReport(null);
        try {
            const params = {};
            if (email.trim()) params.email = email.trim();
            if (empId.trim()) params.emp_id = empId.trim();
            const res = await api.get(
                "/admin/attendance-diagnostic",
                { params },
            );
            setReport(res.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRunning(false);
        }
    };

    const runDateBreakdown = async () => {
        if (!dateInput) {
            toast.error("Pick a date");
            return;
        }
        setRunning(true);
        setSnapshotsSummary(null);
        setMissingSnapshots(null);
        try {
            const [breakdownRes, missingRes] = await Promise.all([
                api.get(
                    "/admin/attendance-diagnostic/snapshots-on-date",
                    { params: { attendance_date: dateInput } },
                ),
                api.get(
                    "/admin/attendance-diagnostic/missing-snapshots-on-date",
                    { params: { attendance_date: dateInput } },
                ),
            ]);
            setSnapshotsSummary(breakdownRes.data);
            setMissingSnapshots(missingRes.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRunning(false);
        }
    };

    const copyText = async () => {
        if (!report?.text) return;
        try {
            await navigator.clipboard.writeText(report.text);
            toast.success("Diagnostic text copied to clipboard");
        } catch {
            toast.error("Clipboard write failed; please select and copy manually");
        }
    };

    // Iter UAT-3 / Phase 2 — Rebuild a single (employee, date) snapshot
    // via the existing POST /api/attendance/snapshot/rebuild endpoint.
    // No new business logic. After success: display the returned snapshot
    // JSON and refresh the 9-block diagnostic so Block 7 reflects the
    // rebuilt row.
    const runRebuildSnapshot = async () => {
        const targetEmpId = report?.summary?.employee_id;
        if (!targetEmpId) {
            toast.error("Run the per-employee diagnostic first.");
            setRebuildConfirmOpen(false);
            return;
        }
        if (!rebuildDate) {
            toast.error("Pick a date to rebuild.");
            return;
        }
        setRebuilding(true);
        setRebuildResult(null);
        try {
            const res = await api.post("/attendance/snapshot/rebuild", {
                employee_id: targetEmpId,
                attendance_date: rebuildDate,
            });
            setRebuildResult(res.data);
            toast.success(
                `Snapshot rebuilt for ${targetEmpId} / ${rebuildDate}`,
            );
            // Refresh the 9-block diagnostic so Block 7 reflects the
            // freshly-rebuilt row.
            try {
                const params = {};
                if (email) params.email = email;
                if (empId) params.emp_id = empId;
                if (!email && !empId) params.emp_id = targetEmpId;
                const refreshed = await api.get(
                    "/admin/attendance-diagnostic",
                    { params },
                );
                setReport(refreshed.data);
            } catch (refreshErr) {
                // Soft-fail — the rebuild succeeded; only the refresh failed.
                toast.warning(
                    "Rebuild succeeded but diagnostic refresh failed: "
                    + formatApiError(refreshErr),
                );
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRebuilding(false);
            setRebuildConfirmOpen(false);
        }
    };

    return (
        <div className="p-6 space-y-6" data-testid="attendance-diagnostic-page">
            <PageHeader
                title="Attendance Diagnostic"
                subtitle="Read-only, PSA-only. Mirrors econ2_diagnostic.py from the container."
            />

            {/* Top banner — scheduler state */}
            {schedulerState && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-slate-800 mb-2">
                        <Info size={16} /> Scheduler — recent nightly reconciliations
                    </div>
                    <div className="text-xs text-slate-700 mb-2">
                        Today (UTC): <code className="font-mono">{schedulerState.today_utc}</code>
                        <span className="mx-2">·</span>
                        Expected cron: <code className="font-mono">{schedulerState.expected_cron}</code>
                    </div>
                    {schedulerState.recent_reconciliation_runs?.length ? (
                        <table className="text-xs w-full">
                            <thead className="text-slate-500">
                                <tr><th className="text-left">attendance_date</th><th className="text-right">snapshots created by reconciler</th><th className="text-left">latest updated_at</th></tr>
                            </thead>
                            <tbody>
                                {schedulerState.recent_reconciliation_runs.map((r) => (
                                    <tr key={r.attendance_date} className="border-t border-slate-200">
                                        <td className="font-mono">{r.attendance_date}</td>
                                        <td className="text-right font-mono">{r.reconciler_snapshots_count}</td>
                                        <td className="font-mono text-slate-600">{r.latest_updated_at || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="text-xs text-amber-700">
                            ⚠ No reconciler-triggered snapshots in the last 7 days. If today is missing from the list, the 02:00 UTC trigger did not fire today (likely a restart timing miss).
                        </div>
                    )}
                    <div className="text-xs text-slate-500 mt-2 italic">{schedulerState.note}</div>
                </div>
            )}

            {/* ── Phase-2 pre-flight — Override inventory ───────────────── */}
            <div
                className="rounded-md border border-indigo-200 bg-indigo-50 p-5"
                data-testid="diagnostic-override-inventory-block"
            >
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <h3 className="text-base font-semibold text-indigo-900">
                            Phase-2 pre-flight — Per-employee attendance-source overrides
                        </h3>
                        <p className="text-xs text-indigo-800 mt-1">
                            Inventories every employee carrying a non-empty
                            {" "}<code>attendance_source_override</code>. Run this
                            in production before flipping the default attendance
                            policy from <code>manual</code> to <code>desktop_agent</code>
                            {" "}so that no intentional per-employee exception is
                            silently overwritten by the policy default. Read-only.
                        </p>
                    </div>
                    <Button
                        onClick={runOverrideInventory}
                        disabled={overrideLoading}
                        data-testid="diagnostic-override-inventory-btn"
                    >
                        {overrideLoading ? "Running…" : "Inventory overrides"}
                    </Button>
                </div>

                {overrideInventory && (
                    <div className="mt-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className={
                                "rounded border p-3 " +
                                (overrideInventory.safe_to_flip_policy_default
                                    ? "border-emerald-200 bg-emerald-50"
                                    : "border-amber-200 bg-amber-50")
                            }>
                                <div className={
                                    "text-[11px] " +
                                    (overrideInventory.safe_to_flip_policy_default
                                        ? "text-emerald-700" : "text-amber-700")
                                }>
                                    Employees with override
                                </div>
                                <div
                                    className={
                                        "text-xl font-mono " +
                                        (overrideInventory.safe_to_flip_policy_default
                                            ? "text-emerald-900" : "text-amber-900")
                                    }
                                    data-testid="diagnostic-override-count"
                                >
                                    {overrideInventory.overridden_count}
                                </div>
                            </div>
                            <div className="rounded border border-slate-200 bg-white p-3">
                                <div className="text-[11px] text-slate-500">Orphans (override + non-eligible status)</div>
                                <div className="text-xl font-mono text-slate-900">
                                    {overrideInventory.orphan_count}
                                </div>
                            </div>
                            <div className={
                                "rounded border p-3 " +
                                (overrideInventory.safe_to_flip_policy_default
                                    ? "border-emerald-200 bg-emerald-50"
                                    : "border-amber-200 bg-amber-50")
                            }>
                                <div className={
                                    "text-[11px] font-semibold " +
                                    (overrideInventory.safe_to_flip_policy_default
                                        ? "text-emerald-800" : "text-amber-800")
                                }>
                                    Verdict
                                </div>
                                <div
                                    className={
                                        "text-sm font-mono mt-1 " +
                                        (overrideInventory.safe_to_flip_policy_default
                                            ? "text-emerald-900" : "text-amber-900")
                                    }
                                    data-testid="diagnostic-override-verdict"
                                >
                                    {overrideInventory.safe_to_flip_policy_default
                                        ? "✓ safe to flip policy default"
                                        : "⚠ review overrides before flipping"}
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="text-xs text-slate-500 mb-1">
                                Distribution of <code>attendance_source_override</code> values:
                            </div>
                            <table className="text-xs">
                                <thead className="text-slate-500">
                                    <tr>
                                        <th className="text-left pr-6">value</th>
                                        <th className="text-right">count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(overrideInventory.distribution || []).map((r, i) => (
                                        <tr key={i} className="border-t border-slate-200">
                                            <td className="font-mono pr-6">
                                                {r.value === null ? "(null)" : (r.value === "" ? "(empty string)" : r.value)}
                                            </td>
                                            <td className="text-right font-mono">{r.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {overrideInventory.overridden_employees?.length ? (
                            <div className="overflow-x-auto">
                                <div className="text-xs text-slate-500 mb-1">
                                    Overridden employees (sorted by name):
                                </div>
                                <table className="text-xs w-full">
                                    <thead className="text-slate-500 bg-slate-50">
                                        <tr>
                                            <th className="text-left px-2 py-1.5">Employee</th>
                                            <th className="text-left px-2 py-1.5">Code</th>
                                            <th className="text-left px-2 py-1.5">Status</th>
                                            <th className="text-left px-2 py-1.5">Department</th>
                                            <th className="text-left px-2 py-1.5">Designation</th>
                                            <th className="text-left px-2 py-1.5">Override value</th>
                                            <th className="text-left px-2 py-1.5">Joining</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overrideInventory.overridden_employees.map((e) => (
                                            <tr key={e.id} className="border-t border-slate-200">
                                                <td className="px-2 py-1.5 font-medium text-slate-900">{e.full_name || "—"}</td>
                                                <td className="px-2 py-1.5 font-mono text-slate-700">{e.employee_code || "—"}</td>
                                                <td className="px-2 py-1.5 font-mono">{e.status}</td>
                                                <td className="px-2 py-1.5 text-slate-700">{e.department || "—"}</td>
                                                <td className="px-2 py-1.5 text-slate-700">{e.designation || "—"}</td>
                                                <td className="px-2 py-1.5 font-mono text-slate-700">{e.attendance_source_override}</td>
                                                <td className="px-2 py-1.5 font-mono text-slate-600">{e.date_of_joining || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : null}

                        <div className="text-[11px] text-slate-500 italic">{overrideInventory.note}</div>
                    </div>
                )}
            </div>

            {/* ── BLOCK A — Per-employee diagnostic ─────────────────────── */}
            <div className="rounded-md border border-slate-200 bg-white p-5">
                <h3 className="text-base font-semibold text-slate-900 mb-3">
                    Per-employee 9-block diagnostic
                </h3>
                <p className="text-xs text-slate-600 mb-4">
                    Provide either the login email OR the employee id. The
                    output mirrors <code>econ2_diagnostic.py</code> exactly
                    so you can paste it back for analysis.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <div>
                        <Label className="text-xs">Login email</Label>
                        <Input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="user@reedos.com"
                            data-testid="diagnostic-input-email"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">OR employee id</Label>
                        <Input
                            value={empId}
                            onChange={(e) => setEmpId(e.target.value)}
                            placeholder="EMP-XXXXXXXX"
                            data-testid="diagnostic-input-emp-id"
                        />
                    </div>
                    <div className="flex items-end">
                        <Button
                            onClick={runEmployee}
                            disabled={running}
                            data-testid="diagnostic-run-employee-btn"
                            className="w-full"
                        >
                            <MagnifyingGlass size={16} className="mr-2" />
                            {running ? "Running…" : "Run diagnostic"}
                        </Button>
                    </div>
                </div>

                {report && (
                    <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-xs text-slate-600">
                                Diagnosis: <strong className="text-slate-900">{report.summary?.explanation || "—"}</strong>
                            </div>
                            <Button
                                size="sm" variant="outline"
                                onClick={copyText}
                                data-testid="diagnostic-copy-btn"
                            >
                                Copy full text
                            </Button>
                        </div>
                        <pre
                            className="text-[11px] leading-snug bg-slate-900 text-slate-100 rounded-md p-4 overflow-x-auto whitespace-pre font-mono"
                            data-testid="diagnostic-output"
                        >
                            {report.text}
                        </pre>

                        {/* Iter UAT-3 / Phase 2 — Rebuild this snapshot
                            (one employee · one date · one click). Uses
                            the existing POST /api/attendance/snapshot/rebuild
                            endpoint. PSA-gated on the backend via
                            employee:admin. No new business logic. */}
                        {report.summary?.employee_id && (
                            <div
                                className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4"
                                data-testid="rebuild-snapshot-block"
                            >
                                <div className="flex flex-wrap items-end gap-3">
                                    <div className="grow min-w-[200px]">
                                        <div className="text-xs font-semibold text-amber-900">
                                            Rebuild this snapshot
                                        </div>
                                        <p className="text-[11px] text-amber-800 mt-0.5">
                                            Re-materialises the snapshot for
                                            this employee on the chosen date
                                            via the existing rebuild endpoint.
                                            Idempotent · audit-logged · manual
                                            events always win · payroll-frozen
                                            rows are preserved.
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="text-xs">Attendance date</Label>
                                        <Input
                                            type="date"
                                            value={rebuildDate}
                                            onChange={(e) => setRebuildDate(e.target.value)}
                                            className="w-44"
                                            data-testid="rebuild-input-date"
                                        />
                                    </div>
                                    <Button
                                        onClick={() => setRebuildConfirmOpen(true)}
                                        disabled={rebuilding || !rebuildDate}
                                        data-testid="rebuild-open-confirm-btn"
                                        className="bg-amber-600 hover:bg-amber-700 text-white"
                                    >
                                        <ArrowsClockwise size={16} className="mr-2" />
                                        {rebuilding ? "Rebuilding…" : "Rebuild this snapshot"}
                                    </Button>
                                </div>

                                {rebuildResult && (
                                    <div className="mt-4" data-testid="rebuild-result-block">
                                        <div className="text-xs font-semibold text-slate-800 mb-1">
                                            Rebuilt snapshot row:
                                        </div>
                                        <pre
                                            className="text-[10px] leading-snug bg-white border border-slate-200 text-slate-900 rounded-md p-3 overflow-x-auto whitespace-pre font-mono max-h-[400px]"
                                            data-testid="rebuild-result-json"
                                        >
                                            {JSON.stringify(rebuildResult, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Confirmation dialog for snapshot rebuild */}
            <AlertDialog
                open={rebuildConfirmOpen}
                onOpenChange={setRebuildConfirmOpen}
            >
                <AlertDialogContent data-testid="rebuild-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm snapshot rebuild</AlertDialogTitle>
                        <AlertDialogDescription className="text-xs space-y-2">
                            <span className="block">
                                You are about to re-materialise the
                                daily attendance snapshot for:
                            </span>
                            <span className="block font-mono bg-slate-100 rounded p-2 text-slate-900">
                                employee_id: {report?.summary?.employee_id || "—"}
                                <br />
                                attendance_date: {rebuildDate}
                            </span>
                            <span className="block">
                                This call is <strong>idempotent</strong>;
                                manual events always win; payroll-frozen
                                rows and existing regularisations are
                                preserved. The action is recorded in
                                <code> audit_logs </code> as
                                <code> attendance_snapshot_rebuilt</code>.
                            </span>
                            <span className="block">
                                Proceed?
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="rebuild-cancel-btn">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={runRebuildSnapshot}
                            disabled={rebuilding}
                            data-testid="rebuild-confirm-btn"
                            className="bg-amber-600 hover:bg-amber-700"
                        >
                            {rebuilding ? "Rebuilding…" : "Yes, rebuild"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* ── BLOCK B — Date-level snapshot breakdown ──────────────── */}
            <div className="rounded-md border border-slate-200 bg-white p-5">
                <h3 className="text-base font-semibold text-slate-900 mb-3">
                    Snapshots on a given date (global breakdown)
                </h3>
                <p className="text-xs text-slate-600 mb-4">
                    Confirms the &quot;only N snapshots generated&quot; observation
                    for a given date and surfaces any
                    {" "}<code>employees.status</code>{" "}drift values that
                    would silently exclude an employee from the nightly
                    reconciler loop.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <div>
                        <Label className="text-xs">Date (YYYY-MM-DD)</Label>
                        <Input
                            type="date"
                            value={dateInput}
                            onChange={(e) => setDateInput(e.target.value)}
                            data-testid="diagnostic-input-date"
                        />
                    </div>
                    <div className="flex items-end">
                        <Button
                            onClick={runDateBreakdown}
                            disabled={running}
                            data-testid="diagnostic-run-date-btn"
                            className="w-full"
                        >
                            {running ? "Running…" : "Inspect date"}
                        </Button>
                    </div>
                </div>

                {snapshotsSummary && (
                    <div className="space-y-3 mt-4">
                        <div className="text-sm">
                            On <code className="font-mono">{snapshotsSummary.attendance_date}</code>:
                            <strong className="text-slate-900 ml-2">{snapshotsSummary.total_snapshots}</strong> snapshots ·
                            eligible employees ({" "}<code>status ∈ [&quot;Active&quot;,&quot;Probation&quot;]</code>{" "}):{" "}
                            <strong className="text-slate-900">{snapshotsSummary.eligible_employees_active_probation_count}</strong>
                        </div>

                        <div>
                            <div className="text-xs text-slate-500 mb-1">Distinct employee.status values in DB:</div>
                            <div className="flex flex-wrap gap-2">
                                {(snapshotsSummary.employees_distinct_status_values || []).map((s) => (
                                    <span
                                        key={String(s)}
                                        className={
                                            "px-2 py-0.5 rounded text-xs font-mono " +
                                            (s === "Active" || s === "Probation"
                                                ? "bg-emerald-100 text-emerald-800"
                                                : "bg-rose-100 text-rose-800")
                                        }
                                    >
                                        {s === null ? "(null)" : String(s)}
                                    </span>
                                ))}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                Values in <span className="text-emerald-700 font-semibold">green</span> are eligible for nightly reconciliation. Values in <span className="text-rose-700 font-semibold">red</span> are silently skipped.
                            </div>
                        </div>

                        <div>
                            <div className="text-xs text-slate-500 mb-1">Snapshot breakdown by status × source_mode × trigger:</div>
                            <table className="text-xs w-full">
                                <thead className="text-slate-500">
                                    <tr>
                                        <th className="text-left">attendance_status</th>
                                        <th className="text-left">source_mode</th>
                                        <th className="text-left">compute_trigger</th>
                                        <th className="text-right">count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(snapshotsSummary.breakdown || []).map((r, idx) => (
                                        <tr key={idx} className="border-t border-slate-200">
                                            <td className="font-mono">{r.attendance_status ?? "—"}</td>
                                            <td className="font-mono">{r.attendance_source_mode ?? "—"}</td>
                                            <td className="font-mono">{r.compute_trigger ?? "—"}</td>
                                            <td className="text-right font-mono">{r.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {snapshotsSummary.sample_snapshots?.length ? (
                            <details>
                                <summary className="text-xs text-slate-600 cursor-pointer">
                                    Sample of first 20 snapshots (raw rows)
                                </summary>
                                <pre className="text-[10px] leading-snug bg-slate-900 text-slate-100 rounded-md p-4 mt-2 overflow-x-auto whitespace-pre font-mono">
                                    {JSON.stringify(snapshotsSummary.sample_snapshots, null, 2)}
                                </pre>
                            </details>
                        ) : null}
                    </div>
                )}
            </div>

            {/* ── BLOCK C — Missing snapshots reconciliation ───────────── */}
            {missingSnapshots && (
                <div
                    className="rounded-md border border-slate-200 bg-white p-5"
                    data-testid="diagnostic-missing-snapshots-block"
                >
                    <h3 className="text-base font-semibold text-slate-900 mb-3">
                        Missing snapshots reconciliation
                    </h3>
                    <p className="text-xs text-slate-600 mb-3">
                        Cross-checks the eligible employee population (status ∈
                        {" "}<code>[&quot;Active&quot;,&quot;Probation&quot;]</code>{" "} on
                        <code className="font-mono"> {missingSnapshots.attendance_date}</code>)
                        against <code>daily_attendance_snapshots</code>.
                        Employees who SHOULD have a row but DO NOT appear here.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <div className="rounded border border-slate-200 bg-slate-50 p-3">
                            <div className="text-[11px] text-slate-500">Eligible (Active/Probation)</div>
                            <div className="text-xl font-mono text-slate-900" data-testid="diagnostic-eligible-count">
                                {missingSnapshots.eligible_count}
                            </div>
                        </div>
                        <div className="rounded border border-emerald-200 bg-emerald-50 p-3">
                            <div className="text-[11px] text-emerald-700">With snapshot</div>
                            <div className="text-xl font-mono text-emerald-900" data-testid="diagnostic-with-snapshot-count">
                                {missingSnapshots.with_snapshot_count}
                            </div>
                        </div>
                        <div className={
                            "rounded border p-3 " +
                            (missingSnapshots.missing_count > 0
                                ? "border-rose-200 bg-rose-50"
                                : "border-slate-200 bg-slate-50")
                        }>
                            <div className={
                                "text-[11px] " +
                                (missingSnapshots.missing_count > 0 ? "text-rose-700" : "text-slate-500")
                            }>
                                Missing snapshot
                            </div>
                            <div
                                className={
                                    "text-xl font-mono " +
                                    (missingSnapshots.missing_count > 0 ? "text-rose-900" : "text-slate-900")
                                }
                                data-testid="diagnostic-missing-count"
                            >
                                {missingSnapshots.missing_count}
                            </div>
                        </div>
                    </div>

                    {missingSnapshots.missing_employees?.length ? (
                        <div className="overflow-x-auto">
                            <table className="text-xs w-full" data-testid="diagnostic-missing-table">
                                <thead className="text-slate-500 bg-slate-50">
                                    <tr>
                                        <th className="text-left px-2 py-1.5">Employee</th>
                                        <th className="text-left px-2 py-1.5">Code</th>
                                        <th className="text-left px-2 py-1.5">Status</th>
                                        <th className="text-left px-2 py-1.5">Department</th>
                                        <th className="text-left px-2 py-1.5">Designation</th>
                                        <th className="text-right px-2 py-1.5">Desktop events</th>
                                        <th className="text-right px-2 py-1.5">Active min</th>
                                        <th className="text-right px-2 py-1.5">Manual events</th>
                                        <th className="text-left px-2 py-1.5">Joining</th>
                                        <th className="text-left px-2 py-1.5">Override</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {missingSnapshots.missing_employees.map((e) => {
                                        const hasActivity = (e.desktop_events || 0) > 0 || (e.manual_events || 0) > 0;
                                        return (
                                            <tr
                                                key={e.id}
                                                className={
                                                    "border-t border-slate-200 " +
                                                    (hasActivity ? "bg-amber-50" : "")
                                                }
                                            >
                                                <td className="px-2 py-1.5 font-medium text-slate-900">{e.full_name || "—"}</td>
                                                <td className="px-2 py-1.5 font-mono text-slate-700">{e.employee_code || "—"}</td>
                                                <td className="px-2 py-1.5 font-mono">{e.status}</td>
                                                <td className="px-2 py-1.5 text-slate-700">{e.department || "—"}</td>
                                                <td className="px-2 py-1.5 text-slate-700">{e.designation || "—"}</td>
                                                <td className="px-2 py-1.5 text-right font-mono">{e.desktop_events ?? 0}</td>
                                                <td className="px-2 py-1.5 text-right font-mono">{e.desktop_active_minutes ?? 0}</td>
                                                <td className="px-2 py-1.5 text-right font-mono">{e.manual_events ?? 0}</td>
                                                <td className="px-2 py-1.5 font-mono text-slate-600">{e.date_of_joining || "—"}</td>
                                                <td className="px-2 py-1.5 font-mono text-slate-600">{e.attendance_source_override || "—"}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-xs text-emerald-700">
                            ✓ All eligible employees have a snapshot for this date.
                        </div>
                    )}
                    <div className="text-[11px] text-slate-500 mt-3 italic">
                        {missingSnapshots.note}
                    </div>
                    <div className="text-[11px] text-amber-700 mt-1">
                        Rows highlighted in amber had agent activity OR a manual event for this date — the reconciler should have produced a snapshot for them.
                    </div>
                </div>
            )}
        </div>
    );
}
